import { Link } from "react-router-dom";
import AccountLayout from "../components/AccountLayout.jsx";
import GradientSummaryCard from "../components/billing/GradientSummaryCard.jsx";
import { useSubscription } from "../hooks/useSubscription.js";
import { usePlanCatalog } from "../hooks/usePlanCatalog.js";
import { useCoachTableCounts } from "../hooks/useCoachTableCounts.js";
import { useCoachClientPlans } from "../hooks/useCoachClientPlans.js";
import { isPayingHeycoachSubscriber } from "../lib/heycoachSubscription.js";
import { StatCardSkeleton } from "../components/loading/SkeletonBlocks.jsx";
import ConnectEarningsSection from "../components/dashboard/ConnectEarningsSection.jsx";
import { renewalPartsFromInstant } from "../lib/stripeInstant.js";
import { useCoachConnectSummary } from "../hooks/useCoachConnectSummary.js";
import { useStripeConnect } from "../hooks/useStripeConnect.js";

function formatUnknownPlanLabel(planId) {
  return String(planId)
    .replace(/[-_]/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function formatCoachSummary(clientTotal, programTotal) {
  const clients = `${clientTotal} client${clientTotal === 1 ? "" : "s"}`;
  const programs = `${programTotal} program${programTotal === 1 ? "" : "s"}`;
  return `${clients} · ${programs}`;
}

function DashboardStatCard({ to, label, value, hint }) {
  return (
    <Link
      to={to}
      className="rounded-[14px] border border-hc-surface-border bg-hc-surface p-4 transition hover:bg-white/5"
    >
      <p className="text-xs text-hc-text-dim">{label}</p>
      <p className="mt-1 text-2xl font-bold tracking-[-0.45px] text-hc-text">{value}</p>
      {hint ? <p className="mt-2 text-xs text-hc-text-dim">{hint}</p> : null}
    </Link>
  );
}

export default function DashboardPage() {
  const { subscription: liveSub, isLoading: subLoading, error: subError } = useSubscription();
  const { findTier } = usePlanCatalog();
  const {
    clientTotal,
    programTotal,
    isLoading: coachCountsLoading,
    error: coachCountsError,
  } = useCoachTableCounts();
  const {
    plans: clientPlans,
    isLoading: clientPlansLoading,
    error: clientPlansError,
  } = useCoachClientPlans();
  const stripeConnect = useStripeConnect();
  const {
    summary: connectSummary,
    connectLoading: connectSummaryConnectLoading,
    summaryLoading: connectSummaryLoading,
    error: connectSummaryError,
    connect,
  } = useCoachConnectSummary();

  const rawPlanId =
    liveSub?.planId != null && String(liveSub.planId).trim()
      ? String(liveSub.planId).trim()
      : null;
  const stripeTierPlan = rawPlanId ? findTier(rawPlanId) : null;
  const subscriptionLooksPaid =
    Boolean(liveSub) && isPayingHeycoachSubscriber(liveSub);
  const [renewDay, renewYear] = renewalPartsFromInstant(liveSub?.currentPeriodEnd);

  const statsLoading = coachCountsLoading || clientPlansLoading;
  const clientPlanTotal = clientPlans.length;

  const planHeadline = subLoading
    ? "—"
    : subscriptionLooksPaid
      ? (stripeTierPlan?.name ??
        (rawPlanId ? formatUnknownPlanLabel(rawPlanId) : "Your subscription"))
      : liveSub
        ? "No active plan"
        : "None";

  const planPillSuffix = subscriptionLooksPaid && renewDay && renewYear
    ? `Renews ${renewDay}, ${renewYear}`
    : subscriptionLooksPaid
      ? String(liveSub?.status ?? "").trim() || "Active"
      : "Choose a plan";

  return (
    <AccountLayout>
      <GradientSummaryCard>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h2 className="mt-1 text-2xl font-extrabold tracking-[0.07px] text-hc-text">
              Your coaching dashboard
            </h2>
            {coachCountsLoading ? (
              <div className="mt-1.5 h-4 w-44 animate-pulse rounded bg-white/10" />
            ) : (
              <p className="mt-1.5 text-sm text-hc-text-dim">
                {formatCoachSummary(clientTotal, programTotal)}
              </p>
            )}
            {subError && (
              <p className="mt-2 max-w-lg text-xs text-hc-error">{subError}</p>
            )}
            {coachCountsError && (
              <p className="mt-2 max-w-lg text-xs text-hc-error">{coachCountsError}</p>
            )}
            {clientPlansError && (
              <p className="mt-2 max-w-lg text-xs text-hc-error">{clientPlansError}</p>
            )}
          </div>

          {subLoading ? (
            <div className="h-7 w-44 animate-pulse rounded-full bg-white/10" />
          ) : (
            <Link
              to="/plan"
              className="inline-flex shrink-0 items-center gap-2 rounded-full border border-hc-surface-border bg-hc-surface/80 px-3 py-1.5 text-xs font-medium text-hc-text-muted transition hover:bg-white/5"
            >
              <span className="capitalize text-hc-text">{planHeadline}</span>
              <span className="text-hc-text-dim">·</span>
              <span className="text-hc-text-dim">{planPillSuffix}</span>
              <i className="icon-chevron-right text-[10px] leading-none opacity-70" aria-hidden="true" />
            </Link>
          )}
        </div>

        <div className="mt-6 flex flex-wrap gap-2">
          <Link
            to="/programs"
            className="inline-flex items-center gap-1.5 rounded-[10px] bg-hc-primary px-4 py-2 text-sm font-semibold text-white transition hover:opacity-90"
          >
            <i className="icon-collection leading-none" aria-hidden="true" />
            View programs
          </Link>
          <Link
            to="/plan"
            className="inline-flex items-center gap-1.5 rounded-[10px] border border-hc-surface-border-strong px-4 py-2 text-sm font-semibold text-hc-text-muted transition hover:bg-white/5"
          >
            <i className="icon-star leading-none" aria-hidden="true" />
            Manage plan
          </Link>
          <Link
            to="/billing"
            className="inline-flex items-center gap-1.5 rounded-[10px] border border-hc-surface-border-strong px-4 py-2 text-sm font-semibold text-hc-text-muted transition hover:bg-white/5"
          >
            <i className="icon-credit-card leading-none" aria-hidden="true" />
            Billing
          </Link>
        </div>
      </GradientSummaryCard>

      <section className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {statsLoading ? (
          <>
            <StatCardSkeleton />
            <StatCardSkeleton />
            <StatCardSkeleton />
          </>
        ) : (
          <>
            <DashboardStatCard
              to="/client-plans"
              label="Active clients"
              value={clientTotal}
              hint="Roster synced from the HeyCoach app"
            />
            <DashboardStatCard
              to="/programs"
              label="Programs"
              value={programTotal}
              hint="Training programs you've built"
            />
            <DashboardStatCard
              to="/client-plans"
              label="Client plans"
              value={clientPlanTotal}
              hint="Plans clients can subscribe to"
            />
          </>
        )}
      </section>

      <ConnectEarningsSection
        summary={connectSummary}
        connect={connect}
        connectLoading={connectSummaryConnectLoading}
        summaryLoading={connectSummaryLoading}
        error={connectSummaryError}
        onBeginOnboarding={stripeConnect.beginOnboarding}
        actionBusy={stripeConnect.actionBusy}
      />
    </AccountLayout>
  );
}
